import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, PackagePlus, Plus } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * FT-3a parity — put a product into rooms from inside the grid. The picker stages one
 * `add_product` op per room, so a product column across 40 rooms is still ONE Save and ONE
 * revision batch. Registering a brand-new product on the scope is a catalogue write with its own
 * history and happens immediately — the rooms it lands in are still staged.
 */
export default function ProductPickerDialog({ projectId, scopes = [], rooms = [], pending,
                                              role, actor, onClose }) {
  const [scope, setScope] = useState(scopes[0] || "");
  const [catalogue, setCatalogue] = useState([]);
  const [search, setSearch] = useState("");
  const [uid, setUid] = useState("");
  const [qty, setQty] = useState("0");
  const [picked, setPicked] = useState({});
  const [newProduct, setNewProduct] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!scope) { setCatalogue([]); return; }
    api.get("/frs-editor/products", { params: { project_id: projectId, scope_type: scope } })
      .then(({ data }) => setCatalogue(data?.products || data || []))
      .catch(() => setCatalogue([]));
  }, [projectId, scope]);

  useEffect(() => { setUid(""); setPicked({}); }, [scope]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogue.slice(0, 60);
    return catalogue.filter((p) => `${p.label || ""} ${p.product_code || ""} ${p.product_description || ""}`
      .toLowerCase().includes(q)).slice(0, 60);
  }, [catalogue, search]);

  const scopeRooms = rooms;
  const product = catalogue.find((p) => p.product_uid === uid);
  const eligible = scopeRooms.filter((r) => !r.uids.includes(uid));

  const registerProduct = async () => {
    if (!newProduct?.product_code?.trim()) { toast.error("Give the product a code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/frs-editor/product-records", {
        project_id: projectId, scope_type: scope,
        product_code: newProduct.product_code.trim(),
        product_description: newProduct.product_description || "",
        unit_of_measure: newProduct.unit_of_measure || "Each",
        role, actor, force: true,
      });
      const { data: list } = await api.get("/frs-editor/products",
        { params: { project_id: projectId, scope_type: scope } });
      setCatalogue(list?.products || list || []);
      if (data.product_uid) setUid(data.product_uid);
      setNewProduct(null);
      toast.success(`${newProduct.product_code} registered on ${scope}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.product?.product_uid) { setUid(d.product.product_uid); setNewProduct(null); toast.info(d.message || "That product already exists — selected it"); }
      else toast.error(typeof d === "string" ? d : d?.message || "Could not register that product");
    }
    setBusy(false);
  };

  const stage = () => {
    const chosen = eligible.filter((r) => picked[r.key]);
    if (!uid || !chosen.length) { toast.error("Pick a product and at least one room"); return; }
    const n = Math.max(parseInt(qty, 10) || 0, 0);
    for (const r of chosen) {
      pending.stage(`ap:${r.key}:${uid}`, {
        op: { op: "add_product", room: r.ref, scope_type: scope, product_uid: uid, qty: n },
        room_label: r.label,
        label: `add ${product?.label || product?.product_code || "product"} × ${n}`,
      });
    }
    toast.success(`${chosen.length} room(s) waiting for Save`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="frs-product-picker">
        <DialogHeader>
          <DialogTitle className="text-base">Add a product to rooms</DialogTitle>
          <DialogDescription className="text-xs">
            Nothing is written until you press Save — this lands in the same batch as your other edits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs">Scope</Label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
                    data-testid="frs-picker-scope"
                    className="h-8 text-xs border border-gray-300 rounded px-2 bg-white">
              {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search..." className="h-8 w-52 text-xs"
                   data-testid="frs-picker-search" />
          </div>

          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100"
               data-testid="frs-picker-list">
            {shown.map((p) => (
              <button key={p.product_uid} type="button"
                      data-testid={`frs-picker-product-${p.product_uid}`}
                      onClick={() => setUid(p.product_uid)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                        uid === p.product_uid ? "bg-blue-50 font-semibold text-blue-900" : "hover:bg-gray-50"}`}>
                <span className="flex-1 min-w-0 truncate" title={p.label}>{p.label || p.product_code}</span>
                <span className="text-[10px] text-gray-500 tabular-nums">{p.rooms} room(s)</span>
              </button>
            ))}
            {shown.length === 0 && (
              <div className="px-3 py-4 text-xs text-gray-500" data-testid="frs-picker-empty">
                No product on {scope || "this scope"} matches.
              </div>
            )}
          </div>

          {newProduct ? (
            <div className="border border-blue-200 bg-blue-50/60 rounded-lg p-2 grid grid-cols-3 gap-2"
                 data-testid="frs-picker-new-form">
              {[["product_code", "Code"], ["product_description", "Description"],
                ["unit_of_measure", "Unit"]].map(([f, label]) => (
                <div key={f}>
                  <Label className="text-[10px] uppercase text-gray-500">{label}</Label>
                  <Input value={newProduct[f]} className="h-8 text-xs"
                         data-testid={`frs-picker-new-${f}`}
                         onChange={(e) => setNewProduct({ ...newProduct, [f]: e.target.value })} />
                </div>
              ))}
              <div className="col-span-3 flex gap-2">
                <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={registerProduct}
                        data-testid="frs-picker-new-save">
                  {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Register on {scope}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => setNewProduct(null)} data-testid="frs-picker-new-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="frs-picker-new-btn"
                    disabled={!scope}
                    onClick={() => setNewProduct({ product_code: "", product_description: "", unit_of_measure: "Each" })}>
              <PackagePlus className="w-3 h-3 mr-1" /> A product that is not on {scope || "this scope"} yet
            </Button>
          )}

          {uid && (
            <>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">Quantity per room</Label>
                  <Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
                         className="h-8 w-24 text-xs" data-testid="frs-picker-qty" />
                </div>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                        data-testid="frs-picker-select-all"
                        onClick={() => setPicked(Object.fromEntries(eligible.map((r) => [r.key, true])))}>
                  Select all {eligible.length} shown
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                        data-testid="frs-picker-clear-all" onClick={() => setPicked({})}>
                  Clear
                </Button>
              </div>

              <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100"
                   data-testid="frs-picker-rooms">
                {scopeRooms.map((r) => {
                  const has = r.uids.includes(uid);
                  return (
                    <label key={r.key} className="flex items-center gap-2 px-3 py-1.5 text-xs"
                           data-testid={`frs-picker-room-${r.key}`}>
                      <input type="checkbox" className="w-4 h-4" disabled={has}
                             checked={!has && !!picked[r.key]}
                             data-testid={`frs-picker-room-check-${r.key}`}
                             onChange={() => setPicked({ ...picked, [r.key]: !picked[r.key] })} />
                      <span className="flex-1 min-w-0 truncate">{r.label}</span>
                      {has ? <span className="text-[10px] text-gray-400">already has it</span>
                       : !r.scopes.includes(scope)
                         ? <span className="text-[10px] text-blue-600">adds {scope} here</span>
                         : null}
                    </label>
                  );
                })}
                {scopeRooms.length === 0 && (
                  <div className="px-3 py-4 text-xs text-gray-500" data-testid="frs-picker-rooms-empty">
                    No room in the current view. Clear the filters first.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="frs-picker-cancel">Cancel</Button>
          <Button onClick={stage} data-testid="frs-picker-stage"
                  disabled={!uid || !eligible.some((r) => picked[r.key])}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add to pending ({eligible.filter((r) => picked[r.key]).length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
